import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ActivePollOption {
  id: string;
  option_text: string;
  order_index: number;
  votes_count: number;
}

export interface ActivePoll {
  id: string;
  title: string;
  question: string;
  allow_change_vote: boolean;
  show_results_before_vote: boolean;
  starts_at: string | null;
  ends_at: string | null;
  options: ActivePollOption[];
  user_voted_option_id: string | null;
  total_votes: number;
}

export function useActivePlatformPoll() {
  return useQuery({
    queryKey: ["platform-poll", "active"],
    queryFn: async (): Promise<ActivePoll | null> => {
      const { data, error } = await (supabase as any).rpc("get_active_platform_poll");
      if (error) throw error;
      if (!data || data.length === 0) return null;
      const row = data[0];
      return {
        ...row,
        options: Array.isArray(row.options) ? row.options : [],
      } as ActivePoll;
    },
    staleTime: 60_000,
  });
}

export function useVotePlatformPoll() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({
      pollId,
      optionId,
      existingVoteOptionId,
    }: {
      pollId: string;
      optionId: string;
      existingVoteOptionId: string | null;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Faça login para votar.");

      if (existingVoteOptionId) {
        // change vote: update the existing row to new option
        const { error } = await (supabase as any)
          .from("platform_poll_votes")
          .update({ option_id: optionId })
          .eq("poll_id", pollId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from("platform_poll_votes")
          .insert({ poll_id: pollId, option_id: optionId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-poll"] });
      toast({ title: "Voto registrado!", description: "Obrigado pela participação." });
    },
    onError: (err: any) => {
      toast({
        title: "Não foi possível votar",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    },
  });
}
